import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, Shield, TrendingUp } from 'lucide-react';
export function CJIPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-123">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-124">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-125">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-126">
            <span className="w-8 h-px bg-teal" data-id="element-127"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-128">
              Civic Judgment Index
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-129"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-130">
            The Analytical Core of Civic Judgment
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-131">
            The Civic Judgment Index (CJI) is the foundational analytical layer
            of the SyNeural platform. It is designed to measure and strengthen
            reasoning quality, decision competence, manipulation resistance, and
            leadership judgment across civic, institutional, and
            high-responsibility environments.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-132">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="prose prose-lg max-w-none" data-id="element-133">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-134">
            Modern societies rely on judgment everywhere, but rarely have a
            clear framework for evaluating it. People are expected to make
            decisions under uncertainty, sort signal from noise, resist
            emotional manipulation, and act with foresight in environments
            shaped by complexity, pressure, and competing narratives.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-135">
            CJI was created as the foundational analytical structure for
            understanding how people reason, how they respond to persuasion and
            pressure, and how decision quality can be strengthened over time.
          </p>
        </motion.div>
      </section>

      {/* Why CJI Exists */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-136">
        <div className="max-w-4xl mx-auto" data-id="element-137">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-138">
            <div className="flex items-center gap-4 mb-6" data-id="element-139">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-140">
                <Target size={24} data-id="element-141" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-142">
                Why CJI Exists
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-143">
              Judgment is one of the most important variables in modern society,
              yet institutions often evaluate credentials, outputs, or
              compliance without examining the underlying quality of reasoning
              that shapes decisions. Weak foresight, emotional reactivity, and
              manipulation vulnerability can create consequences far beyond a
              single moment. CJI exists to provide a more structured way to
              assess the core dimensions of judgment that matter in civic,
              professional, institutional, and leadership settings.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What CJI Measures */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-144">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-145">
          <div className="flex items-center gap-4 mb-6" data-id="element-146">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-147">
              <Brain size={24} data-id="element-148" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-149">
              What CJI Measures
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mb-8" data-id="element-150">
            CJI focuses on the key dimensions that shape civic judgment and
            practical decision-making. These include:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-151">
            {['Reasoning quality', 'Consequential thinking', 'Manipulation resistance', 'Emotional regulation', 'Civic reliability', 'Leadership diagnostics'].map((item, index) => <motion.div key={item} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="flex items-center gap-3 bg-white p-4 rounded-lg border border-line" data-id="element-152">
                <div className="w-2 h-2 rounded-full bg-navy" data-id="element-153"></div>
                <span className="text-ink-dark font-medium" data-id="element-154">{item}</span>
              </motion.div>)}
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mt-8" data-id="element-155">
            Together, these domains help create a more rigorous picture of how
            judgment functions under complexity, pressure, and responsibility.
          </p>
        </motion.div>
      </section>

      {/* The Role of CJI */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-156">
        <div className="max-w-4xl mx-auto" data-id="element-157">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-158">
            <div className="flex items-center gap-4 mb-6" data-id="element-159">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-160">
                <Shield size={24} data-id="element-161" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-162">
                The Role of CJI in the Platform
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-163">
              CJI serves as the analytical base layer of SyNeural. It is the
              platform's core engine for decision-quality analytics,
              propaganda-resistance profiling, cognitive pattern assessment, and
              leadership reasoning diagnostics. Where NCJI expands into broader
              developmental pathways, lifecycle architecture, and institutional
              deployment, CJI provides the essential measurement logic that
              makes those higher-order applications possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How CJI Creates Value */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-164">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-165">
          <div className="flex items-center gap-4 mb-6" data-id="element-166">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-167">
              <TrendingUp size={24} data-id="element-168" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-169">
              How CJI Creates Value
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-170">
            CJI is designed to make judgment more visible, more structured, and
            more actionable. It helps individuals, institutions, and leadership
            environments better understand where reasoning is strong, where
            vulnerabilities exist, and where development is needed. By
            identifying patterns, clarifying risks, and supporting stronger
            decision-making, CJI creates a more useful analytical foundation for
            growth and readiness.
          </p>
        </motion.div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-171">
        <div className="max-w-5xl mx-auto" data-id="element-172">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-173">
            <h2 className="text-3xl font-heading font-bold text-ink-dark mb-12 text-center" data-id="element-174">
              Use Cases
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-175">
              {[{
              title: 'Education',
              desc: 'Support reasoning, media literacy, and judgment development'
            }, {
              title: 'Employers & Organizations',
              desc: 'Strengthen decision quality, communication, and advancement readiness'
            }, {
              title: 'Civil Society',
              desc: 'Support misinformation resilience and collective reasoning capacity'
            }, {
              title: 'Public Institutions',
              desc: 'Structured framework for understanding readiness and judgment quality'
            }, {
              title: 'Leadership Settings',
              desc: 'High-responsibility decision-making and executive evaluation'
            }, {
              title: 'Civic Environments',
              desc: 'Strengthen public discourse and democratic participation'
            }].map((useCase, index) => <motion.div key={useCase.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white p-6 rounded-lg border border-line" data-id="element-176">
                  <h3 className="text-lg font-heading font-bold text-ink-dark mb-2" data-id="element-177">
                    {useCase.title}
                  </h3>
                  <p className="text-ink-muted text-sm leading-relaxed" data-id="element-178">
                    {useCase.desc}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CJI and NCJI */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-179">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-navy-deep text-white p-12 rounded-2xl" data-id="element-180">
          <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-181">CJI and NCJI</h2>
          <p className="text-lg leading-relaxed opacity-90" data-id="element-182">
            Within SyNeural's broader ecosystem, CJI serves as the core
            analytical layer. NCJI builds on that foundation to create a wider
            developmental, institutional, and leadership-readiness system.
            Together, they form a unified architecture for understanding,
            strengthening, and scaling judgment across the full human and
            institutional spectrum.
          </p>
        </motion.div>
      </section>
    </div>;
}